/* tslint:disable */
// This client has been automatically generated by @kempoweroyj/ce-cli-generate-rest-client@1.0.15
import { Injectable } from '@angular/core';
import { AuthTokenService } from '@kempoweroyj/ngx-auth-token-service';
import { HttpClient } from '@angular/common/http';

interface RequestOptions {
    headers?: { [key: string]: string };
}

@Injectable()
export class FactoryApiService {
    constructor(
        private http: HttpClient,
        private authTokenService: AuthTokenService
    ) {}

    /** Returns a copy of the Factory API client that uses the given JWT for authorization */
    withToken(jwt: string) {
        return new FactoryApiService(this.http, { ...this.authTokenService, jwt });
    }

    /**
     * Register a charging station to Kempower cloud and retrieve connection token for it
     */
    async factoryRegisterChargingStation(
        body: FactoryRegisterChargingStationRequest,
        options?: RequestOptions
    ): Promise<FactoryRegisterChargingStationResponse> {
        return this.http
            .post<FactoryRegisterChargingStationResponse>(
                `${this.authTokenService.restApiUrl}/factory/register`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Register a charging station to Kempower cloud and retrieve connection token for it
     */
    async factoryChargingStationStatus(
        serialNumber: string,
        options?: RequestOptions
    ): Promise<FactoryChargingStationStatusResponse> {
        return this.http
            .get<FactoryChargingStationStatusResponse>(
                `${this.authTokenService.restApiUrl}/factory/status/${encodeURIComponent(serialNumber)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Triggers a firmware update for a charging station
     */
    async factoryTriggerFirmwareUpdate(
        serialNumber: string,
        options?: RequestOptions
    ): Promise<FactoryTriggerFirmwareUpdateResponse> {
        return this.http
            .post<FactoryTriggerFirmwareUpdateResponse>(
                `${this.authTokenService.restApiUrl}/factory/firmware/trigger/${encodeURIComponent(serialNumber)}`,
                undefined,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Check status of the latest firmware update
     */
    async factoryFirmwareStatus(
        serialNumber: string,
        options?: RequestOptions
    ): Promise<FactoryFirmwareStatusResponse> {
        return this.http
            .get<FactoryFirmwareStatusResponse>(
                `${this.authTokenService.restApiUrl}/factory/firmware/status/${encodeURIComponent(serialNumber)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Request a charging station to be restarted
     */
    async factoryRestart(serialNumber: string, options?: RequestOptions): Promise<FactoryRestartResponse> {
        return this.http
            .post<FactoryRestartResponse>(
                `${this.authTokenService.restApiUrl}/factory/restart/${encodeURIComponent(serialNumber)}`,
                undefined,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Generates Roaming EVSE IDs based on an order.
     */
    async generateEvseIdsForOrder(
        orderNumber: string,
        body: GenerateEvseIdsForOrderRequest,
        options?: RequestOptions
    ): Promise<GenerateEvseIdsForOrderResponse> {
        return this.http
            .post<GenerateEvseIdsForOrderResponse>(
                `${this.authTokenService.restApiUrl}/orders/${encodeURIComponent(orderNumber)}/generateEvseIds`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Fetch relevant locations for the given given customer id, used by label printer to populate the locations dropdown
     */
    async getRelevantLocationsForCustomer(
        customerId: string,
        options?: RequestOptions
    ): Promise<GetRelevantLocationsForCustomerResponse> {
        return this.http
            .get<GetRelevantLocationsForCustomerResponse>(
                `${this.authTokenService.restApiUrl}/orders/byCustomer/${encodeURIComponent(customerId)}/locations`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get equipment orders from Kempower
     */
    async getOrders(options?: RequestOptions): Promise<GetOrdersResponse> {
        return this.http
            .get<GetOrdersResponse>(`${this.authTokenService.restApiUrl}/orders`, {
                headers: {
                    Authorization: `Bearer ${this.authTokenService.jwt}`,
                    ...(options?.headers || {}),
                },
            })
            .toPromise();
    }

    /**
     * Get equipment orders from Kempower
     */
    async getOrdersOrderNumber(orderNumber: string, options?: RequestOptions): Promise<GetOrdersResponse> {
        return this.http
            .get<GetOrdersResponse>(`${this.authTokenService.restApiUrl}/orders/${encodeURIComponent(orderNumber)}`, {
                headers: {
                    Authorization: `Bearer ${this.authTokenService.jwt}`,
                    ...(options?.headers || {}),
                },
            })
            .toPromise();
    }

    /**
     * Get equipment orders from Kempower
     */
    async getOrdersCustomer(customerId: string, options?: RequestOptions): Promise<GetOrdersResponse> {
        return this.http
            .get<GetOrdersResponse>(
                `${this.authTokenService.restApiUrl}/orders/byCustomer/${encodeURIComponent(customerId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get equipment orders from Kempower
     */
    async getOrdersLocation(locationId: string, options?: RequestOptions): Promise<GetOrdersResponse> {
        return this.http
            .get<GetOrdersResponse>(
                `${this.authTokenService.restApiUrl}/orders/byLocation/${encodeURIComponent(locationId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Get station related equipment orders from Kempower
     */
    async getOrdersStation(stationId: string, options?: RequestOptions): Promise<GetOrdersStationResponse> {
        return this.http
            .get<GetOrdersStationResponse>(
                `${this.authTokenService.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/order`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }

    /**
     * Given orderNumber, generate evse labels pdf
     */
    async generateLabelsPdf(
        orderNumber: string,
        body: GenerateLabelsPdfRequest,
        options?: RequestOptions
    ): Promise<GenerateLabelsPdfResponse> {
        return this.http
            .post<GenerateLabelsPdfResponse>(
                `${this.authTokenService.restApiUrl}/orders/${encodeURIComponent(orderNumber)}/pdf`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authTokenService.jwt}`,
                        ...(options?.headers || {}),
                    },
                }
            )
            .toPromise();
    }
}

export interface CustomerLocation {
    address: string;
    customerLocationId: string;
    name: string;
}

export interface FactoryChargingStationStatusResponse {
    connectedToCloud: boolean;
    firmwareVersion: string;
}

export interface FactoryFirmwareStatusResponse {
    /**
     * if true, update process is completed, no longer pending. Either success or fail.
     */
    completed?: boolean;
    /**
     * If failed or cancelled!
     */
    failed?: boolean;
    id: number;
    state: UpdateProcessState;
    /**
     * If completed successfully
     */
    success?: boolean;
}

export interface FactoryRegisterChargingStationRequest {
    /**
     * ID a customer has given to this station
     */
    customerSuppliedId?: string;
    productCode: string;
    serialNumber: string;
}

export interface FactoryRegisterChargingStationResponse {
    stationId: string;
    success: true;
    token: string;
}

export interface FactoryRestartResponse {
    success: true;
}

export interface FactoryTriggerFirmwareUpdateResponse {
    success: true;
    updateProcessId: number;
}

export interface GenerateEvseIdsForOrderRequest {
    customerLocationId?: string;
}

export interface GenerateEvseIdsForOrderResponse {
    roamingEvseIds: GeneratedEvseId[];
}

export interface GenerateLabelsPdfRequest {
    printEvseNumbers?: string;
}

export interface GenerateLabelsPdfResponse {
    url: string;
}

export interface GeneratedEvseId {
    evseNumber: number;
    roamingEvseId: string;
}

export interface GetOrdersResponse {
    orders: SalesOrderEntry[];
}

export interface GetOrdersStationResponse {
    orders: PartialSalesOrderEntry[];
}

export interface GetRelevantLocationsForCustomerResponse {
    locations: CustomerLocation[];
}

export interface PartialSalesOrderEntry {
    company?: string;
    customerId?: string;
    customerLocationId?: string;
    customerName?: string;
    generatedEvseIds?: GeneratedEvseId[];
    isEquipmentOrder?: boolean;
    lines?: SalesOrderLine[];
    open?: boolean;
    orderDate?: string;
    orderNumber?: string;
    po?: string;
    shipToAddress?: string;
    void?: boolean;
}

export interface SalesOrderEntry {
    company: string;
    customerId: string;
    customerLocationId?: string;
    customerName: string;
    generatedEvseIds?: GeneratedEvseId[];
    isEquipmentOrder: boolean;
    lines: SalesOrderLine[];
    open: boolean;
    orderDate: string;
    orderNumber: string;
    po: string;
    shipToAddress: string;
    void: boolean;
}

export interface SalesOrderLine {
    description: string;
    isEquipmentOrder: boolean;
    lineNo: number;
    open: boolean;
    partNum: string;
    qty: number;
    void: boolean;
    warrantyCode: string;
}

export type UpdateProcessState =
    | 'Cancelled'
    | 'CertificateVerified'
    | 'DownloadFailed'
    | 'DownloadPaused'
    | 'DownloadScheduled'
    | 'Downloaded'
    | 'Downloading'
    | 'Idle'
    | 'InstallRebooting'
    | 'InstallScheduled'
    | 'InstallVerificationFailed'
    | 'InstallationFailed'
    | 'Installed'
    | 'Installing'
    | 'InvalidCertificate'
    | 'InvalidSignature'
    | 'NotStarted'
    | 'PublishFailed'
    | 'RevokedCertificate'
    | 'SignatureVerified'
    | 'Started';
